import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, CheckCircle2, Circle, Loader2, MessageSquarePlus, Euro, Save,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmtEur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

export default function ContractDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newStage, setNewStage] = useState("");
    const [stageNote, setStageNote] = useState("");
    const [savingStage, setSavingStage] = useState(false);
    const [note, setNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    const [commAmount, setCommAmount] = useState("");
    const [productSummary, setProductSummary] = useState("");

    const load = () => {
        api.get(`/contracts/${id}`)
            .then(({ data }) => {
                setC(data);
                setCommAmount(data.commission_amount ?? "");
                setProductSummary(data.product_summary || "");
            })
            .catch((e) => toast.error(e.response?.data?.detail || "Contratto non trovato"))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const advanceStage = async () => {
        if (!newStage) { toast.error("Seleziona una fase"); return; }
        setSavingStage(true);
        try {
            const { data } = await api.patch(`/admin/contracts/${id}/stage`,
                { stage: newStage, note: stageNote || null });
            setC(data);
            setNewStage("");
            setStageNote("");
            toast.success("Fase aggiornata");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore aggiornamento");
        } finally { setSavingStage(false); }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setSavingNote(true);
        try {
            const { data } = await api.post(`/contracts/${id}/notes`, { text: note.trim() });
            setC(data);
            setNote("");
            toast.success("Nota aggiunta");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally { setSavingNote(false); }
    };

    const saveCommission = async () => {
        try {
            const { data } = await api.patch(`/admin/contracts/${id}/commission`, {
                commission_amount: commAmount === "" ? null : Number(commAmount),
                product_summary: productSummary || null,
            });
            setC(data);
            toast.success("Riepilogo salvato");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };

    if (loading) {
        return <div className="card p-10 flex justify-center max-w-3xl mx-auto"><Loader2 className="w-6 h-6 animate-spin text-[#0C4650]" /></div>;
    }
    if (!c) return null;

    const stages = c.stages_available || [];
    const historyByStage = {};
    for (const h of c.stages_history || []) historyByStage[h.stage] = h;
    const currentIdx = stages.findIndex((s) => s.key === c.current_stage);
    const backHref = isAdmin ? "/admin?tab=contracts" : "/contratti";

    return (
        <div className="max-w-4xl mx-auto space-y-5" data-testid="contract-detail">
            <Link to={backHref} className="inline-flex items-center gap-1 text-sm font-bold text-[#0C4650] hover:underline">
                <ArrowLeft className="w-4 h-4" /> Torna ai contratti
            </Link>

            <div className="card p-6">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">CONTRATTO</div>
                        <h1 className="text-2xl sm:text-3xl font-black text-[#0C4650] mt-1">
                            {c.client_name || "—"}
                        </h1>
                        <div className="text-sm text-gray-600 mt-1">
                            {c.client_email} {c.client_phone && `· ${c.client_phone}`}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">PROVVIGIONE</div>
                        <div className="text-2xl font-black text-[#0C4650]">
                            {fmtEur(c.commission_amount ?? c.commission_expected ?? 0)}
                        </div>
                        {c.commission_paid_at ? (
                            <div className="text-xs font-bold text-emerald-600 mt-1">LIQUIDATA · {fmtDate(c.commission_paid_at)}</div>
                        ) : c.commission_expected != null && (
                            <div className="text-xs text-gray-500">prevista</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Timeline fasi */}
            <div className="card p-6" data-testid="stages-timeline">
                <h2 className="font-black text-[#0C4650] mb-4">Fasi di lavorazione</h2>
                <div className="space-y-4">
                    {stages.map((s, idx) => {
                        const hist = historyByStage[s.key];
                        const done = idx <= currentIdx;
                        const current = idx === currentIdx;
                        return (
                            <div key={s.key} className="flex items-start gap-3" data-testid={`stage-${s.key}`}>
                                <div className="flex flex-col items-center shrink-0">
                                    {done ? (
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${current ? "bg-[#C7FF2C] text-[#0C4650]" : "bg-emerald-500 text-white"}`}>
                                            <CheckCircle2 className="w-4 h-4" />
                                        </div>
                                    ) : (
                                        <div className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-300">
                                            <Circle className="w-4 h-4" />
                                        </div>
                                    )}
                                    {idx < stages.length - 1 && (
                                        <div className={`w-0.5 flex-1 min-h-8 mt-1 ${done && idx < currentIdx ? "bg-emerald-400" : "bg-gray-200"}`} />
                                    )}
                                </div>
                                <div className="flex-1 pb-3">
                                    <div className={`font-bold ${current ? "text-[#0C4650]" : done ? "text-gray-800" : "text-gray-400"}`}>
                                        {s.label}
                                        {current && <span className="ml-2 text-[10px] bg-[#C7FF2C] text-[#0C4650] px-2 py-0.5 rounded-full font-black">FASE ATTUALE</span>}
                                    </div>
                                    {hist && (
                                        <div className="text-xs text-gray-500 mt-1">
                                            {fmtDate(hist.changed_at)} · {hist.actor_name || "—"}
                                            {hist.note && <div className="mt-1 text-gray-700 italic">"{hist.note}"</div>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Admin: cambio fase */}
                {isAdmin && (
                    <div className="mt-5 border-t border-gray-100 pt-4">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D] mb-2">
                            AVANZA FASE
                        </div>
                        <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                            <select value={newStage} onChange={(e) => setNewStage(e.target.value)}
                                    className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                                    data-testid="admin-select-stage">
                                <option value="">Seleziona fase…</option>
                                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                            <button onClick={advanceStage} disabled={savingStage || !newStage}
                                    className="btn-primary disabled:opacity-50"
                                    data-testid="admin-advance-stage">
                                {savingStage ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiorna"}
                            </button>
                        </div>
                        <input value={stageNote} onChange={(e) => setStageNote(e.target.value)}
                               placeholder="Nota (opzionale) per l'agente"
                               className="mt-2 w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                               data-testid="admin-stage-note" />
                    </div>
                )}
            </div>

            {/* Admin: riepilogo economico */}
            {isAdmin && (
                <div className="card p-6" data-testid="commission-panel">
                    <h2 className="font-black text-[#0C4650] mb-3 flex items-center gap-2">
                        <Euro className="w-4 h-4" /> Riepilogo economico
                    </h2>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-bold text-[#0C4650] block mb-1">Provvigione confermata (€)</label>
                            <input type="number" step="0.01" value={commAmount}
                                   onChange={(e) => setCommAmount(e.target.value)}
                                   className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                                   data-testid="commission-amount-input"
                                   placeholder={`Prevista: ${fmtEur(c.commission_expected || 0)}`} />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#0C4650] block mb-1">Riepilogo prodotto confermato</label>
                            <textarea rows={3} value={productSummary}
                                      onChange={(e) => setProductSummary(e.target.value)}
                                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                                      data-testid="product-summary-input"
                                      placeholder="Es. Impianto FTV 5 kW + accumulo 10 kWh · Fornitore XYZ" />
                        </div>
                        <button onClick={saveCommission} className="btn-primary" data-testid="save-commission-btn">
                            <Save className="w-4 h-4" /> Salva
                        </button>
                    </div>
                </div>
            )}

            {/* Agente/admin: riepilogo read-only */}
            {!isAdmin && c.product_summary && (
                <div className="card p-6">
                    <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">PRODOTTO CONFERMATO</div>
                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{c.product_summary}</div>
                </div>
            )}

            {/* Note */}
            <div className="card p-6" data-testid="notes-panel">
                <h2 className="font-black text-[#0C4650] mb-3 flex items-center gap-2">
                    <MessageSquarePlus className="w-4 h-4" /> Note e comunicazioni
                </h2>
                <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
                    {(c.notes || []).length === 0 ? (
                        <div className="text-sm text-gray-400">Nessuna nota ancora.</div>
                    ) : c.notes.map((n) => (
                        <div key={n.id}
                             className={`p-3 rounded-lg text-sm ${n.author_role === "admin" ? "bg-[#0C4650]/5 border-l-4 border-[#0C4650]" : "bg-[#C7FF2C]/20 border-l-4 border-[#C7FF2C]"}`}
                             data-testid={`note-${n.id}`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="font-bold text-[#0C4650]">{n.author_name} <span className="text-[10px] font-normal text-gray-500 ml-1">{n.author_role}</span></span>
                                <span className="text-[10px] text-gray-500">{fmtDate(n.created_at)}</span>
                            </div>
                            <div className="text-gray-700 whitespace-pre-wrap">{n.text}</div>
                        </div>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)}
                           placeholder="Scrivi una nota…"
                           className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                           data-testid="new-note-input" />
                    <button onClick={addNote} disabled={savingNote || !note.trim()}
                            className="btn-primary disabled:opacity-50"
                            data-testid="add-note-btn">
                        {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia"}
                    </button>
                </div>
            </div>
        </div>
    );
}
